import type { GetCurrentMemberFn, GetTeamMemberFn } from '@/features/iam'
import type { Job } from '@/features/jobs/Job'
import { isNotNil } from '@/utils/object-util'

/**
 * Whether the current user may submit the draft job.
 */
export function canSubmitDraftJob(
  job: Job,
  getCurrentMember: GetCurrentMemberFn,
  getTeamMember: GetTeamMemberFn,
) {
  const currentMember = getCurrentMember(job.workspace.id)
  if (currentMember.permissions.adminJobCreate) {
    return true
  }

  if (job.ownerTeam) {
    return (
      getTeamMember(job.ownerTeam.id, currentMember.user.publicId)?.permissions
        .createTeamJobs ?? false
    )
  }

  return Boolean(job.currentUserCollaborator?.member.permissions.startNewJob)
}

/**
 * Whether the current user may see the job's price. Outside of admins, this
 * is decided by their team members on the job's teams, or by their workspace
 * member when they are on none of them.
 */
export function canViewJobPrice(
  job: Job,
  getCurrentMember: GetCurrentMemberFn,
  getTeamMember: GetTeamMemberFn,
) {
  const currentMember = getCurrentMember(job.workspace.id)
  const { permissions } = currentMember
  if (permissions.adminJobView || permissions.adminViewPricing) {
    return true
  }

  const teamMembers = job.teams
    .map((team) => getTeamMember(team.id, currentMember.user.publicId))
    .filter(isNotNil)
  return teamMembers.length > 0
    ? teamMembers.some((tm) => tm.permissions.viewJobPricing)
    : permissions.viewJobPricing
}
